import { Component, EventEmitter, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { configurationEnvironment } from '@configuration/environment/environment.configuration';
import { environment } from '@environment/environment';
import { ModalDirective } from '@shared/directives/modal.directive';
import { IHiringFirm } from '@shared/interfaces/hiring-firm.interface';
import { ISetting } from '@shared/interfaces/setting.interface';
import { IntegrationsBullhorn } from '@shared/models/integrations/integrations.bullhorn.model';
import { IntegrationsERecruit } from '@shared/models/integrations/integrations.erecruit.model';
import { IntegrationsGreenhouse } from '@shared/models/integrations/integrations.greenhouse.model';
import { IntegrationsJobAdder } from '@shared/models/integrations/integrations.jobadder.model';
import { IntegrationsJobDiva } from '@shared/models/integrations/integrations.jobdiva.model';
import { IntegrationsSalesforceIsv } from '@shared/models/integrations/integrations.salesforce-isv.model';
import { IntegrationsSalesforce } from '@shared/models/integrations/integrations.salesforce.model';
import { IntegrationsTargetRecruit } from '@shared/models/integrations/integrations.target-recruit.model';
import { IntegrationsBullhornService } from '@shared/services/integrations/integrations.bullhorn.service';
import { IntegrationsErecruitService } from '@shared/services/integrations/integrations.erecruit.service';
import { IntegrationsGreenhouseService } from '@shared/services/integrations/integrations.greenhouse.service';
import { IntegrationsJobAdderService } from '@shared/services/integrations/integrations.jobadder.service';
import { IntegrationsJobDivaService } from '@shared/services/integrations/integrations.jobdiva.service';
import { IntegrationsMockAtsService } from '@shared/services/integrations/integrations.mock-ats.service';
import { IntegrationsSalesforceIsvService } from '@shared/services/integrations/integrations.salesforce-isv.service';
import { IntegrationsSalesforceService } from '@shared/services/integrations/integrations.salesforce.service';
import { IntegrationsTargetRecruitService } from '@shared/services/integrations/integrations.target-recruit.service';
import {
  SpongeConfigService,
  SpongeAtsConfig
} from '@shared/services/integrations/sponge-config.service';
import {
  SpongeDiagnosticProbe,
  SpongeDiagnosticsReport,
  SpongeDiagnosticStatus,
  SpongeIntegrationService,
  SpongeIntegrationStatus
} from '@shared/services/integrations/sponge-integration.service';
import { WorkableService, WorkableWebhookSettings } from '@shared/services/integrations/workable.service';
import { TranslateService } from '@ngx-translate/core';
import { TimeoutError } from 'rxjs';
import { LoggerService } from '@shared/services/logger.service';
import { ToastService } from '@shared/services/toast.service';
import { SessionStorage } from 'ngx-webstorage';
import { ClipboardService } from 'ngx-clipboard';

type SpongeCredential = SpongeAtsConfig['authentication']['credentials'][number];

/**
 * Custom validator to ensure input is a subdomain, not a full domain
 * Valid: "acme-corp", "my-company", "test123"
 * Invalid: "www.vitay.io", "acme-corp.com", "https://example.com"
 */
function subdomainValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;

  if (!value) {
    return null; // Let required validator handle empty values
  }

  // Check for common domain indicators
  const invalidPatterns = [
    /^https?:\/\//i,        // Contains http:// or https://
    /\./,                    // Contains a dot (domain separator)
    /^www\./i,               // Starts with www.
    /@/,                     // Contains @ symbol
    /\s/,                    // Contains whitespace
    /[\/\\]/                 // Contains slashes
  ];

  for (const pattern of invalidPatterns) {
    if (pattern.test(value)) {
      return { subdomain: true };
    }
  }

  // Valid subdomain pattern: alphanumeric, hyphens, underscores
  const validSubdomainPattern = /^[a-z0-9][a-z0-9_-]*[a-z0-9]$/i;

  if (!validSubdomainPattern.test(value)) {
    return { subdomain: true };
  }

  return null;
}

@Component({
    selector: 'app-modals-integrations',
    templateUrl: './modals.integrations.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ModalsIntegrationsComponent
  extends ModalDirective<any>
  implements OnInit
{
  @Output()
  public getHiringFirmIntegrations: EventEmitter<any> = new EventEmitter();

  @SessionStorage() private hiringFirm: IHiringFirm;
  @SessionStorage() public settings: ISetting;

  public ENDPOINTS_ASSETS = String(environment.ENDPOINTS.ASSETS);
  public id: string;
  public credentials:
    | IntegrationsBullhorn
    | IntegrationsJobAdder
    | IntegrationsJobDiva
    | IntegrationsERecruit
    | IntegrationsSalesforce
    | IntegrationsSalesforceIsv
    | IntegrationsTargetRecruit;
  public isButtonsDisabled = Boolean(false);
  public description: string;
  public isSetup = Boolean(false);
  public isAvailable = Boolean(false);
  public entry: FormGroup;
  public title: string;
  public logo: string;
  public config: any;
  public webhookUrl: string;
  public isSpongeIntegration: boolean = false;
  public spongeConfig: SpongeAtsConfig | null = null;
  public embedUrl: string | null = null;
  public listActionUrl: string | null = null;
  public isLegacyConnected: boolean = false;
  public hasWebhookSubscription: boolean = false;

  // Workable webhook settings
  public webhookSettings: WorkableWebhookSettings | null = null;
  public webhookSettingsForm: FormGroup | null = null;
  public isLoadingWebhookSettings = false;
  public isSavingWebhookSettings = false;
  public workableStages: string[] = ['Sourced', 'Applied', 'Phone Screen', 'Assessment', 'Interview', 'Offer', 'Hired'];
  public isLoadingStages = false;
  private saveWebhookSettingsTimeout: any = null;

  // Workable recruiter assignment
  public recruiters: any[] = [];
  public isLoadingRecruiters = false;

  // Connection diagnostics ("Verify connection"), see supportsDiagnostics
  public diagnostics: SpongeDiagnosticsReport | null = null;
  public diagnosticsError: string | null = null;
  public isDiagnosticsRunning: boolean = false;

  private readonly constructorName: string = String(this.constructor.name);

  // Legacy integrations list
  // 'bullhorn' is intentionally absent — whether it routes onto the Sponge
  // branch below is decided per-firm at runtime by the
  // `is_bullhorn_sponge_enabled` setting (see ngOnInit). The `case
  // 'bullhorn':` blocks left in setVariables()/onSubmit() are LIVE for any
  // firm where that flag is off/unset — they are the exact pre-Sponge
  // legacy flow. On the Sponge path the credential form is rebuilt from
  // the YAML config, whose control names differ from the legacy ones
  // (api_username/api_password vs username/password) — so the template
  // must render the dynamic Sponge block, never the hardcoded
  // *ngSwitchCase="'bullhorn'" block.
  private readonly LEGACY_INTEGRATIONS = [
    'erecruit',
    'jobdiva',
    'jobadder',
    'salesforce',
    'salesforce_isv',
    'target_recruit',
    'greenhouse',
    'mock_ats'
  ];

  constructor(
    private readonly _fb: FormBuilder,
    private readonly _logger: LoggerService,
    private readonly _toast: ToastService,
    private readonly _jobadder: IntegrationsJobAdderService,
    private readonly _salesforce: IntegrationsSalesforceService,
    private readonly _salesforce_isv: IntegrationsSalesforceIsvService,
    private readonly _spongeConfig: SpongeConfigService,
    private readonly _sponge: SpongeIntegrationService,
    private readonly _workable: WorkableService,
    private readonly _clipboard: ClipboardService,
    private readonly _translate: TranslateService,

    // Integrations services
    public readonly _erecruit: IntegrationsErecruitService,
    public readonly _jobdiva: IntegrationsJobDivaService,
    public readonly _bullhorn: IntegrationsBullhornService,
    public readonly _target_recruit: IntegrationsTargetRecruitService,
    public readonly _greenhouse: IntegrationsGreenhouseService,
    public readonly _mock_ats: IntegrationsMockAtsService
  ) {
    super();
  }

  public get submitKey() {
    let submitKey = 'BUTTONS.SUBMIT';
    switch (this.id) {
      case 'salesforce_isv':
      case 'salesforce':
      case 'jobadder':
        submitKey = 'MODALS.INTEGRATIONS.JOBADDER.SUBMITKEY';
        break;

      case 'target_recruit':
      case 'bullhorn':
      case 'erecruit':
      case 'jobdiva':
      case 'greenhouse':
      case 'mock_ats':
      default:
        submitKey = 'BUTTONS.SUBMIT';
        break;
    }

    return submitKey;
  }

  ngOnInit(): void {
    this.openModal.subscribe((res: any) => {
      this.id = res.id;
      this.logo = res.logo;
      this.isSetup = res.isSetup;
      this.isAvailable = res.isAvailable;
      this.embedUrl = null;
      this.listActionUrl = null;
      this.isLegacyConnected = false;
      this.resetDiagnostics();

      // Check if this is a Sponge integration (not in legacy list).
      // Bullhorn is a special case: it's only on the Sponge flow for firms
      // where the is_bullhorn_sponge_enabled setting is on. Flag off/unset
      // (the default) keeps Bullhorn on the legacy path, pixel for pixel
      // with pre-Sponge prod behavior.
      const isLegacyBullhorn =
        this.id === 'bullhorn' && !this.settings?.is_bullhorn_sponge_enabled;
      this.isSpongeIntegration =
        !this.LEGACY_INTEGRATIONS.includes(this.id) && !isLegacyBullhorn;

      if (this.isSpongeIntegration) {
        // Load Sponge config dynamically
        this._spongeConfig.getConfig(this.id).subscribe(
          (config: SpongeAtsConfig) => {
            this.spongeConfig = config;
            this.setVariables();

            if (this.isSetup) {
              this.loadStatus();
            }
          },
          (err: any) => {
            this._logger.error(
              this.constructorName,
              `Load config for ${this.id}`,
              err
            );
            this._toast.error('TOASTS.INTEGRATION_CONFIG_LOAD_FAILED');
          }
        );
      } else {
        // Legacy integration - use hardcoded config
        if (!environment.IS_PRODUCTION) {
          switch (this.id) {
            case 'bullhorn':
              this.entry.patchValue(
                configurationEnvironment.INTEGRATIONS.BULLHORN
              );
              break;

            case 'erecruit':
              this.entry.patchValue(
                configurationEnvironment.INTEGRATIONS.ERECRUIT
              );
              break;

            case 'jobdiva':
              this.entry.patchValue(
                configurationEnvironment.INTEGRATIONS.JOBDIVA
              );
              break;

            case 'jobadder':
              this.entry.patchValue(
                configurationEnvironment.INTEGRATIONS.JOBADDER
              );
              break;

            case 'salesforce':
              this.entry.patchValue(
                configurationEnvironment.INTEGRATIONS.SALESFORCE
              );
              break;

            case 'salesforce_isv':
              this.entry.patchValue(
                configurationEnvironment.INTEGRATIONS.SALESFORCE_ISV
              );
              break;

            case 'target_recruit':
              this.entry.patchValue(
                configurationEnvironment.INTEGRATIONS.TARGET_RECRUIT
              );
              break;

            case 'greenhouse':
              this.entry.patchValue(
                configurationEnvironment.INTEGRATIONS.GREENHOUSE
              );
              break;
          }
        }

        this.setVariables();
      }
    });

    this.createForm();
  }

  public copyEmbedUrl(): void {
    if (!this.embedUrl) {
      return;
    }

    this._clipboard.copy(this.embedUrl);
    this._toast.success('TOASTS.COPIED_TO_CLIPBOARD');
  }

  public get cardUrl(): string | null {
    if (!this.embedUrl) {
      return null;
    }

    return this.embedUrl.replace('/embed/bullhorn?', '/embed/bullhorn/card?');
  }

  public copyCardUrl(): void {
    if (!this.cardUrl) {
      return;
    }

    this._clipboard.copy(this.cardUrl);
    this._toast.success('TOASTS.COPIED_TO_CLIPBOARD');
  }

  public copyWebhookUrl(): void {
    if (!this.webhookUrl) {
      return;
    }

    this._clipboard.copy(this.webhookUrl);
    this._toast.success('TOASTS.WEBHOOK_URL_COPIED');
  }

  // Menu-action URL: the tab URL plus the view=form deep link (embed URLs end in '&' so the
  // ATS appends its own params after ours).
  public get menuUrl(): string | null {
    if (!this.embedUrl) {
      return null;
    }

    return `${this.embedUrl}view=form&`;
  }

  public copyMenuUrl(): void {
    if (!this.menuUrl) {
      return;
    }

    this._clipboard.copy(this.menuUrl);
    this._toast.success('TOASTS.COPIED_TO_CLIPBOARD');
  }

  public copyListActionUrl(): void {
    if (!this.listActionUrl) {
      return;
    }

    this._clipboard.copy(this.listActionUrl);
    this._toast.success('TOASTS.COPIED_TO_CLIPBOARD');
  }

  /**
   * A connected Sponge integration whose config advertises the diagnostics
   * endpoint (e.g. Workday: ISU permission probes) gets a "Verify connection"
   * button. Not set up = nothing to probe yet.
   */
  public get supportsDiagnostics(): boolean {
    return (
      this.isSpongeIntegration &&
      this.isSetup &&
      Boolean(this.spongeConfig?.frontend?.supports_diagnostics)
    );
  }

  public runDiagnostics(): void {
    if (this.isDiagnosticsRunning) {
      return;
    }

    const url = `GET /api/sponge/${this.id}/diagnostics`;
    this.diagnostics = null;
    this.diagnosticsError = null;
    this.isDiagnosticsRunning = true;

    this._sponge.runDiagnostics(this.id).subscribe(
      (report: SpongeDiagnosticsReport) => {
        this._logger.info(this.constructorName, url, report);
        this.diagnostics = report;
        this.isDiagnosticsRunning = false;
      },
      (err: any) => {
        this._logger.error(this.constructorName, url, err);
        // A client-side timeout or a gateway 504 both mean "the ATS didn't
        // answer in time", not that the connection is broken
        const timedOut = err instanceof TimeoutError || err?.status === 504;
        this.diagnosticsError = timedOut
          ? this._translate.instant('MODALS.INTEGRATIONS.DIAGNOSTICS.TIMEOUT')
          : err?.error?.message ||
            this._translate.instant('MODALS.INTEGRATIONS.DIAGNOSTICS.FAILED');
        this.isDiagnosticsRunning = false;
      }
    );
  }

  public diagnosticsAlertClass(overall: SpongeDiagnosticsReport['overall']): string {
    switch (overall) {
      case 'pass':
        return 'alert-success';
      case 'warn':
        return 'alert-warning';
      default:
        return 'alert-danger';
    }
  }

  public probeBadgeClass(status: SpongeDiagnosticStatus): string {
    switch (status) {
      case 'pass':
        return 'bg-success';
      case 'fail':
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  }

  /**
   * Sponge configs (config/integrations/*.yml on the API) carry English
   * credential labels, help text, descriptions and probe labels. Rather than
   * localising the YAML, the frontend keeps translations under
   * MODALS.INTEGRATIONS.SPONGE.<ATS_KEY> / DIAGNOSTICS.PROBES and falls back
   * to the backend text for anything the locale doesn't know (VIT-1412).
   */
  public credentialLabel(credential: SpongeCredential): string {
    return this.translateOr(
      this.credentialKey(credential, 'LABEL'),
      credential.label || credential.name
    );
  }

  public credentialHelp(credential: SpongeCredential): string {
    return this.translateOr(
      this.credentialKey(credential, 'HELP'),
      credential.help_text || ''
    );
  }

  public probeLabel(probe: SpongeDiagnosticProbe): string {
    return this.translateOr(
      `MODALS.INTEGRATIONS.DIAGNOSTICS.PROBES.${probe.key.toUpperCase()}`,
      probe.label
    );
  }

  // Workable stage names are the identifiers the webhook payloads carry, so
  // the value stays raw and only the label is translated
  public stageLabel(stage: string): string {
    return this.translateOr(
      `MODALS.INTEGRATIONS.WEBHOOKS.STAGES.${stage.toUpperCase().replace(/\s+/g, '_')}`,
      stage
    );
  }

  private credentialKey(credential: SpongeCredential, leaf: 'LABEL' | 'HELP'): string {
    return `MODALS.INTEGRATIONS.SPONGE.${this.id.toUpperCase()}.CREDENTIALS.${credential.name.toUpperCase()}.${leaf}`;
  }

  private hasTranslation(key: string): boolean {
    const value = this._translate.instant(key);
    return typeof value === 'string' && value !== key;
  }

  private translateOr(key: string, fallback: string): string {
    return this.hasTranslation(key) ? this._translate.instant(key) : fallback;
  }

  private resetDiagnostics(): void {
    this.diagnostics = null;
    this.diagnosticsError = null;
    this.isDiagnosticsRunning = false;
  }

  private loadStatus(): void {
    const url = `GET /api/sponge/${this.id}/status`;

    this._sponge.getStatus(this.id).subscribe(
      (status: SpongeIntegrationStatus) => {
        this.embedUrl = status.embed_url;
        this.webhookUrl = status.webhook_url;
        this.listActionUrl = status.list_action_url ?? null;
        this.isLegacyConnected = status.legacy;

        // Check if webhook subscription exists (for Workable)
        if (this.id === 'workable_webhook' && status.has_webhook_subscription !== undefined) {
          this.hasWebhookSubscription = status.has_webhook_subscription;
        }

        // Load Workable webhook settings
        if (this.id === 'workable_webhook') {
          this.loadWorkableStages();
          this.loadWorkableWebhookSettings();
        }
      },
      (err: any) => {
        this._logger.error(this.constructorName, url, err);
      }
    );
  }

  public delete(): void {
    const url = `DELETE ${this.id}`;

    // A firm still connected via the pre-Sponge Bullhorn integration has
    // no Sponge row to delete — route it to the legacy delete path
    // instead, preserving pre-migration behavior. Sponge-connected
    // (non-legacy) bullhorn falls through to the sponge branch below.
    if (this.isSpongeIntegration && this.isLegacyConnected && this.id === 'bullhorn') {
      this._bullhorn.delete().subscribe(
        (res: any) => {
          this._logger.info(this.constructorName, url, res);
          this.onDismiss();
          this.credentials = null;
          this.closeModal.next();
        },
        (err: any) => {
          this._logger.error(this.constructorName, url, err);
        }
      );
      return;
    }

    if (this.isSpongeIntegration) {
      // Handle Sponge integrations
      this._sponge.delete(this.id).subscribe(
        (res: any) => {
          this._logger.info(this.constructorName, url, res);
          this._toast.success('TOASTS.INTEGRATION_REMOVED', { name: this.spongeConfig?.ats_name || this.id });

          this.onDismiss();
          this.credentials = null;
          this.closeModal.next();
        },
        (err: any) => {
          this._logger.error(this.constructorName, url, err);
          this._toast.error(
            'TOASTS.INTEGRATION_REMOVE_FAILED',
            { name: this.spongeConfig?.ats_name || this.id }
          );
        }
      );
    } else {
      // Handle legacy integrations
      this[`_${this.id}`].delete().subscribe(
        (res: any) => {
          this._logger.info(this.constructorName, url, res);
          this.onDismiss();
          this.credentials = null;
          this.closeModal.next();
        },
        (err: any) => {
          this._logger.error(this.constructorName, url, err);
        }
      );
    }
  }

  public onDismiss() {
    this.entry.reset();

    const controls = this.entry.controls;

    Object.entries(controls).map((c) => {
      c[1].setValidators([]);
      c[1].updateValueAndValidity();
    });

    this.getHiringFirmIntegrations.emit();
  }

  public onSubmit({ value, valid }: { value: any; valid: boolean }) {
    if (valid) {
      this.isButtonsDisabled = true;
      this.entry.disable();

      // Handle Sponge integrations
      if (this.isSpongeIntegration) {
        // OAuth2 authorization-code ATS: store credentials server-side,
        // then redirect the browser to the ATS authorization page.
        if (this.spongeConfig && this._spongeConfig.requiresOAuth(this.spongeConfig)) {
          const oauthUrl = `POST /api/sponge/${this.id}/oauth/prepare`;
          this._sponge.initiateOAuth(this.id, value, this.spongeConfig).subscribe(
            () => {
              // Browser is being redirected to the ATS authorization page
            },
            (err: any) => {
              this._logger.error(this.constructorName, oauthUrl, err);
              this._toast.error(
                'TOASTS.INTEGRATION_AUTHORIZATION_START_FAILED',
                { name: this.spongeConfig?.ats_name || this.id }
              );

              this.errors = err;
              this.isButtonsDisabled = false;
              this.entry.enable();
            }
          );
          return;
        }

        const url = `POST /api/sponge/${this.id}/authorize`;
        this._sponge.authorize(this.id, value).subscribe(
          (res: any) => {
            this._logger.info(this.constructorName, url, res);
            this._toast.success('TOASTS.INTEGRATION_ADDED', { name: this.spongeConfig?.ats_name || this.id });
            // Credentials accepted but something is still to do on the ATS
            // side (e.g. a Workday ISU domain not granted yet) - the
            // connection is saved, the diagnostics will name the gap
            if (res?.warning) {
              this._toast.warning(res.warning);
            }

            this.entry.enable();
            this.isButtonsDisabled = false;

            // Close modal and trigger refresh
            this.closeModal.next();
            this.getHiringFirmIntegrations.emit();
          },
          (err: any) => {
            this._logger.error(this.constructorName, url, err);
            this._toast.error(
              'TOASTS.INTEGRATION_ADD_FAILED',
              { name: this.spongeConfig?.ats_name || this.id }
            );

            this.errors = err;
            this.isButtonsDisabled = false;
            this.entry.enable();
          }
        );
        return;
      }

      // Legacy integrations
      switch (this.id) {
        case 'bullhorn':
          this.credentials = new IntegrationsBullhorn({
            ...this.credentials,
            ...value
          });
          break;

        case 'erecruit':
          this.credentials = new IntegrationsERecruit({
            ...this.credentials,
            ...value
          });
          break;

        case 'jobadder':
          this.credentials = new IntegrationsJobAdder({
            ...this.credentials,
            ...value,
            hiring_firm_id: this.hiringFirm.id,
            redirect_uri: this._jobadder.REDIRECT_URI
          });
          break;

        case 'jobdiva':
          this.credentials = new IntegrationsJobDiva({
            ...this.credentials,
            ...value
          });
          break;

        case 'salesforce':
          this.credentials = new IntegrationsSalesforce({
            ...this.credentials,
            ...value,
            hiring_firm_id: this.hiringFirm.id,
            redirect_uri: this._salesforce.REDIRECT_URI
          });
          break;

        case 'salesforce_isv':
          this.credentials = new IntegrationsSalesforceIsv({
            ...this.credentials,
            ...value,
            hiring_firm_id: this.hiringFirm.id,
            redirect_uri: this._salesforce_isv.REDIRECT_URI
          });
          break;

        case 'target_recruit':
          this.credentials = new IntegrationsTargetRecruit({
            ...this.credentials,
            ...value
          });
          break;

        case 'greenhouse':
          this.credentials = new IntegrationsGreenhouse({
            ...this.credentials,
            ...value
          });
          break;

        case 'mock_ats':
          this.credentials = {
            ...value,
            hiring_firm_id: this.hiringFirm.id
          };
          break;
      }

      if (
        this.id === 'jobadder' ||
        this.id === 'salesforce' ||
        this.id === 'salesforce_isv' ||
        this.id === 'mock_ats'
      ) {
        const url = this[`_${this.id}`].generateOAuthLink(
          this.credentials as any
        );

        window.open(url, '_self');
      } else {
        const url = `POST ${this.id}`;
        this[`_${this.id}`].authorize(this.credentials).subscribe(
          (res: any) => {
            this._logger.info(this.constructorName, url, res);
            this._toast.success('TOASTS.INTEGRATION_ADDED', { name: this.id });

            this.entry.enable();
            this.isButtonsDisabled = false;
            this.credentials = null;

            this.resetModal();
          },
          (err: any) => {
            this._logger.error(this.constructorName, url, err);
            this._toast.error('TOASTS.INTEGRATION_FAILED', { name: this.id });

            this.errors = err;
            this.isButtonsDisabled = false;
            this.entry.enable();
          }
        );
      }
    }
  }

  protected createForm() {
    this.entry = this._fb.group({
      // erecruit
      site_url: [''],
      attachment_type_id: [''],
      folder_group_id: [''],
      ad_source_id: [''],
      status_id: [''],
      requirement_id: [''],
      department_id: [''],

      // Bullhorn, erecruit, target recruit
      client_id: [''],
      client_secret: [''],
      username: [''],
      password: [''],

      // target recruit
      candidate_record_type_id: [''],
      security_token: [''],

      // Greenhouse
      api_key: [''],
      user_id: ['']
    });
  }

  protected setVariables() {
    // Handle Sponge integrations dynamically
    if (this.isSpongeIntegration && this.spongeConfig) {
      this.title =
        this.spongeConfig.frontend?.display_name || this.spongeConfig.ats_name;
      // The template pipes `description` through translate: hand it the
      // locale key when one exists, else the backend's English text
      const descriptionKey = `MODALS.INTEGRATIONS.SPONGE.${this.id.toUpperCase()}.DESCRIPTION`;
      this.description = this.hasTranslation(descriptionKey)
        ? descriptionKey
        : this.spongeConfig.description || '';

      // Create dynamic form controls based on YAML credentials
      const formControls: any = {};
      this.spongeConfig.authentication.credentials.forEach((cred: any) => {
        const validators = cred.required ? [Validators.required] : [];

        // Add subdomain validator for Workable subdomain field
        if (this.id === 'workable_webhook' && cred.name === 'subdomain') {
          validators.push(subdomainValidator);
        }

        formControls[cred.name] = ['', validators];
      });

      this.entry = this._fb.group(formControls);
      return;
    }

    // Legacy integrations - use hardcoded config
    // Recreate the form to ensure it has all legacy fields
    this.createForm();

    this.title = `INTEGRATIONS.${this.id.toUpperCase()}.TITLE`;
    this.description = `INTEGRATIONS.${this.id.toUpperCase()}.DESCRIPTION`;

    const siteUrl = this.entry.controls['site_url'];
    const attachmentTypeId = this.entry.controls['site_url'];
    const folderGroupId = this.entry.controls['folder_group_id'];
    const adSourceId = this.entry.controls['ad_source_id'];
    const statusId = this.entry.controls['status_id'];
    const requirementId = this.entry.controls['requirement_id'];
    const candidateRecordTypeId =
      this.entry.controls['candidate_record_type_id'];
    const securityToken = this.entry.controls['security_token'];
    const departmentId = this.entry.controls['department_id'];

    const clientId = this.entry.controls['client_id'];
    const clientSecret = this.entry.controls['client_secret'];

    const username = this.entry.controls['username'];
    const password = this.entry.controls['password'];
    const userId = this.entry.controls['user_id'];
    const apiKey = this.entry.controls['api_key'];

    switch (this.id) {
      case 'bullhorn':
        siteUrl.setValidators([]);
        attachmentTypeId.setValidators([]);
        folderGroupId.setValidators([]);
        adSourceId.setValidators([]);
        statusId.setValidators([]);
        requirementId.setValidators([]);
        candidateRecordTypeId.setValidators([]);
        securityToken.setValidators([]);
        departmentId.setValidators([]);

        clientId.setValidators([Validators.required]);
        clientSecret.setValidators([Validators.required]);

        username.setValidators([Validators.required]);
        password.setValidators([Validators.required]);
        break;

      case 'erecruit':
        siteUrl.setValidators([Validators.required]);
        attachmentTypeId.setValidators([Validators.required]);
        folderGroupId.setValidators([Validators.required]);
        adSourceId.setValidators([Validators.required]);
        statusId.setValidators([Validators.required]);
        requirementId.setValidators([Validators.required]);
        candidateRecordTypeId.setValidators([]);
        securityToken.setValidators([]);
        departmentId.setValidators([]);

        clientId.setValidators([Validators.required]);
        clientSecret.setValidators([Validators.required]);

        username.setValidators([]);
        password.setValidators([]);
        break;

      case 'jobdiva':
        siteUrl.setValidators([]);
        attachmentTypeId.setValidators([]);
        folderGroupId.setValidators([]);
        adSourceId.setValidators([]);
        statusId.setValidators([]);
        requirementId.setValidators([]);
        candidateRecordTypeId.setValidators([]);
        securityToken.setValidators([]);
        departmentId.setValidators([]);

        clientId.setValidators([Validators.required]);
        clientSecret.setValidators([]);

        username.setValidators([Validators.required]);
        password.setValidators([Validators.required]);
        break;

      case 'target_recruit':
        siteUrl.setValidators([Validators.required]);
        attachmentTypeId.setValidators([]);
        folderGroupId.setValidators([]);
        adSourceId.setValidators([]);
        statusId.setValidators([]);
        requirementId.setValidators([]);
        candidateRecordTypeId.setValidators([Validators.required]);
        securityToken.setValidators([]);
        departmentId.setValidators([]);

        clientId.setValidators([Validators.required]);
        clientSecret.setValidators([Validators.required]);

        username.setValidators([Validators.required]);
        password.setValidators([Validators.required]);
        break;

      case 'greenhouse':
        siteUrl.setValidators([Validators.required]);
        attachmentTypeId.setValidators([]);
        folderGroupId.setValidators([]);
        adSourceId.setValidators([]);
        statusId.setValidators([]);
        requirementId.setValidators([]);
        candidateRecordTypeId.setValidators([]);
        securityToken.setValidators([]);
        departmentId.setValidators([]);

        clientId.setValidators([]);
        clientSecret.setValidators([]);

        username.setValidators([]);
        password.setValidators([]);

        userId.setValidators([Validators.required]);
        apiKey.setValidators([Validators.required]);

        this._greenhouse.getWebhook().subscribe((res: any) => {
          this.webhookUrl = res.url;
        });

        break;

      case 'mock_ats':
        siteUrl.setValidators([]);
        attachmentTypeId.setValidators([]);
        folderGroupId.setValidators([]);
        adSourceId.setValidators([]);
        statusId.setValidators([]);
        requirementId.setValidators([]);
        candidateRecordTypeId.setValidators([]);
        securityToken.setValidators([]);
        departmentId.setValidators([]);

        clientId.setValidators([Validators.required]);
        clientSecret.setValidators([Validators.required]);

        username.setValidators([]);
        password.setValidators([]);

        userId.setValidators([]);
        apiKey.setValidators([]);

        break;
    }

    siteUrl.updateValueAndValidity();
    attachmentTypeId.updateValueAndValidity();
    folderGroupId.updateValueAndValidity();
    adSourceId.updateValueAndValidity();
    statusId.updateValueAndValidity();
    requirementId.updateValueAndValidity();
    candidateRecordTypeId.updateValueAndValidity();
    securityToken.updateValueAndValidity();
    departmentId.updateValueAndValidity();

    clientId.updateValueAndValidity();
    clientSecret.updateValueAndValidity();

    username.updateValueAndValidity();
    password.updateValueAndValidity();
  }

  /**
   * Load Workable webhook settings
   */
  private loadWorkableWebhookSettings(): void {
    this.isLoadingWebhookSettings = true;

    this._workable.getWebhookSettings().subscribe(
      (response) => {
        this.webhookSettings = response.data.webhook_settings;
        this.buildWebhookSettingsForm();
        this.isLoadingWebhookSettings = false;

        // Load recruiters for the dropdown
        this.loadRecruiters();
      },
      (err: any) => {
        this._logger.error(this.constructorName, 'loadWorkableWebhookSettings', err);
        this._toast.error('TOASTS.WEBHOOK_SETTINGS_LOAD_FAILED');
        this.isLoadingWebhookSettings = false;
      }
    );
  }

  /**
   * Load recruiters for the hiring firm
   */
  private loadRecruiters(): void {
    this.isLoadingRecruiters = true;

    this._workable.getRecruiters().subscribe(
      (response: any) => {
        this.recruiters = response.data || [];
        this.isLoadingRecruiters = false;

        // If no default recruiter is set, default to the default_root
        if (!this.webhookSettingsForm?.get('default_recruiter_id')?.value) {
          const defaultRoot = this.recruiters.find(r => r.is_default_root);
          if (defaultRoot) {
            this.webhookSettingsForm?.patchValue({
              default_recruiter_id: defaultRoot.id
            });
          }
        }
      },
      (err: any) => {
        this._logger.error(this.constructorName, 'loadRecruiters', err);
        this._toast.error('TOASTS.RECRUITERS_LOAD_FAILED');
        this.isLoadingRecruiters = false;
      }
    );
  }

  /**
   * Load unique stages from Workable account
   */
  private loadWorkableStages(): void {
    this.isLoadingStages = true;

    this._workable.getStages().subscribe(
      (response) => {
        const stages = response.data.stages || [];
        // Only update if we got stages, otherwise keep the hardcoded fallback
        if (stages.length > 0) {
          this.workableStages = stages;
        }
        this.isLoadingStages = false;
      },
      (err: any) => {
        // Silently fall back to hardcoded list on error
        this._logger.error(this.constructorName, 'loadWorkableStages', err);
        this.isLoadingStages = false;
      }
    );
  }

  /**
   * Build webhook settings form
   */
  private buildWebhookSettingsForm(): void {
    if (!this.webhookSettings) return;

    this.webhookSettingsForm = this._fb.group({
      candidate_created: [this.webhookSettings.candidate_created],
      candidate_moved: [this.webhookSettings.candidate_moved],
      candidate_deleted: [this.webhookSettings.candidate_deleted],
      enabled_stages: [this.webhookSettings.enabled_stages || ['Assessment']],
      default_recruiter_id: [(this.webhookSettings as any).default_recruiter_id || null]
    });
  }

  /**
   * Check if a stage is enabled
   */
  public isStageEnabled(stage: string): boolean {
    if (!this.webhookSettingsForm) return false;
    const enabledStages = this.webhookSettingsForm.get('enabled_stages')?.value || [];
    return enabledStages.includes(stage);
  }

  /**
   * Toggle stage selection and auto-save
   */
  public onStageToggle(stage: string, event: any): void {
    if (!this.webhookSettingsForm) return;

    const enabledStages = this.webhookSettingsForm.get('enabled_stages')?.value || [];
    const isChecked = event.target.checked;

    if (isChecked && !enabledStages.includes(stage)) {
      this.webhookSettingsForm.patchValue({
        enabled_stages: [...enabledStages, stage]
      });
    } else if (!isChecked && enabledStages.includes(stage)) {
      this.webhookSettingsForm.patchValue({
        enabled_stages: enabledStages.filter((s: string) => s !== stage)
      });
    }

    // Auto-save after stage selection change
    this.onWebhookSettingChange();
  }

  /**
   * Handle webhook setting change - debounced auto-save
   */
  public onWebhookSettingChange(): void {
    if (!this.webhookSettingsForm) return;

    // Clear any existing timeout
    if (this.saveWebhookSettingsTimeout) {
      clearTimeout(this.saveWebhookSettingsTimeout);
    }

    // Auto-save after a short delay to debounce rapid changes
    this.saveWebhookSettingsTimeout = setTimeout(() => {
      this.saveWorkableWebhookSettings();
      this.saveWebhookSettingsTimeout = null;
    }, 500);
  }

  /**
   * Handle default recruiter change
   */
  public onDefaultRecruiterChange(): void {
    this.onWebhookSettingChange();
  }

  /**
   * Save Workable webhook settings
   */
  private saveWorkableWebhookSettings(): void {
    if (!this.webhookSettingsForm || !this.webhookSettingsForm.valid) return;
    if (this.isSavingWebhookSettings) return; // Prevent concurrent saves

    this.isSavingWebhookSettings = true;
    const settings: WorkableWebhookSettings = this.webhookSettingsForm.value;

    this._workable.updateWebhookSettings(settings).subscribe(
      (response: any) => {
        // Check if there was a partial success (settings saved but webhook sync failed)
        if (response?.warning) {
          this._toast.success(response.message || 'TOASTS.WEBHOOK_SETTINGS_SAVED_SYNC_WARNING');
          this._logger.info(this.constructorName, 'saveWorkableWebhookSettings - partial success', response.warning);
        } else {
          this._toast.success('TOASTS.WEBHOOK_SETTINGS_SAVED');
        }

        this.isSavingWebhookSettings = false;
        this.webhookSettings = settings;
        this.webhookSettingsForm.markAsPristine();
      },
      (err: any) => {
        this._logger.error(this.constructorName, 'saveWorkableWebhookSettings', err);
        this._toast.error('TOASTS.WEBHOOK_SETTINGS_SAVE_FAILED');
        this.isSavingWebhookSettings = false;
      }
    );
  }
}
